import { memo, useMemo, type MouseEvent } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripCitationMarkers } from "../lib/citationMarkers";
import { linkedAgentFileFromHref, resolveLinkedAgentFile, safeMarkdownImageSource } from "../lib/filePreview";
import type { AgentFile } from "../types/api";

interface Props {
  text: string;
  streaming?: boolean;
  className?: string;
  linkedFiles?: AgentFile[];
  onFileLink?: (file: AgentFile, context: MarkdownFileLinkContext) => void;
}

export interface MarkdownFileLinkContext {
  files: AgentFile[];
}

function markdownComponents(onFileLink: Props["onFileLink"], context: MarkdownFileLinkContext): Components {
  return {
    a({ node: _node, href, children, ...props }) {
      if (!href) return <>{children}</>;
      const candidate = linkedAgentFileFromHref(href);
      const linkedFile = candidate ? resolveLinkedAgentFile(candidate, context.files) : null;
      if (linkedFile) {
        return (
          <a
            href={href}
            data-agent-file-link="true"
            {...props}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              onFileLink?.(linkedFile, context);
            }}
          >
            {children}
          </a>
        );
      }
      if (candidate) return <span data-agent-file-unavailable="true">{children}</span>;
      const external = /^https?:\/\//i.test(href);
      return (
        <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} {...props}>
          {children}
        </a>
      );
    },
    img({ node: _node, src, alt, ...props }) {
      if (!src) return null;
      const safeSrc = safeMarkdownImageSource(src);
      if (typeof safeSrc !== "string") return <span className="markdown-image-blocked">{alt ?? "[blocked image]"}</span>;
      return <img src={safeSrc} alt={alt ?? ""} loading="lazy" className="markdown-image" {...props} />;
    },
    table({ node: _node, children, ...props }) {
      return (
        <div className="markdown-table-wrap">
          <table {...props}>{children}</table>
        </div>
      );
    }
  };
}

// Memoized so that re-rendering the transcript (e.g. on every streaming delta of
// the latest turn) doesn't re-parse the Markdown of every other bubble — the
// main scroll/stream jank in long conversations.
export const MarkdownMessage = memo(function MarkdownMessage({
  text,
  streaming = false,
  className = "",
  linkedFiles = [],
  onFileLink
}: Props) {
  const components = useMemo(
    () => markdownComponents(onFileLink, { files: linkedFiles }),
    [linkedFiles, onFileLink]
  );
  const shown = useMemo(() => stripCitationMarkers(text, { streaming }), [text, streaming]);
  return (
    <div className={`bubble-markdown ${className}`.trim()}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {shown}
      </ReactMarkdown>
      {streaming ? <span className="caret" /> : null}
    </div>
  );
});
